import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { useCallback } from "react";

/**
 * Shared configuration and user feedback for dictation, so every surface that
 * embeds `SpeechInput` behaves and reports failures identically.
 */
export const useSpeechInput = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();

  const handlePermissionDenied = useCallback(() => {
    toast({
      variant: "warning",
      title: t("chat.input.speech.permissionDenied.title"),
      description: t("chat.input.speech.permissionDenied.description"),
    });
  }, [toast, t]);

  const handleError = useCallback(() => {
    toast({
      variant: "warning",
      title: t("chat.input.speech.error.title"),
      description: t("chat.input.speech.error.description"),
    });
  }, [toast, t]);

  /**
   * The connection dropped mid-dictation. Said out loud because the recording
   * survives it — without this the user sees the transcript stop and assumes
   * the session is lost, then reloads and actually loses it.
   */
  const handleConnectionLost = useCallback(() => {
    toast({
      variant: "warning",
      title: t("chat.input.speech.connectionLost.title"),
      description: t("chat.input.speech.connectionLost.description"),
    });
  }, [toast, t]);

  return {
    speechLanguage: i18n.language === "fr" ? "fr" : "en",
    handlePermissionDenied,
    handleError,
    handleConnectionLost,
  };
};
